import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Users,
  Target,
  Heart,
  Shield,
  CheckCircle,
  Phone,
  Star
} from "lucide-react";

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We operate with honesty and transparency in everything we do, from pricing to service delivery."
  },
  {
    icon: Award,
    title: "Excellence",
    description: "We strive for excellence in every project, never cutting corners or compromising on quality."
  },
  {
    icon: Heart,
    title: "Customer Focus",
    description: "Your satisfaction is our priority. We listen, adapt, and deliver results that exceed expectations."
  },
  {
    icon: Target,
    title: "Reliability",
    description: "We show up on time, complete work as promised, and stand behind our services with our guarantee."
  }
];

const team = [
  {
    name: "James Mitchell",
    role: "Founder & CEO",
    description: "With over 20 years in the industry, James founded BuyVistas with a vision to provide premium pressure washing services."
  },
  {
    name: "Sarah Chen",
    role: "Operations Manager",
    description: "Sarah ensures smooth operations and maintains our high standards of customer service across all projects."
  },
  {
    name: "Marcus Johnson",
    role: "Lead Technician",
    description: "Marcus leads our field team with expertise in both residential and commercial pressure washing solutions."
  }
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="about-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1552197717-fdea1c70c5c7?q=80&w=2070"
            alt="Professional team"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sky-400 font-bold uppercase tracking-widest text-sm mb-4 block"
            >
              About Us
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="heading-primary text-white mb-6"
            >
              New York's Most Trusted Pressure Washing Company
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-300"
            >
              For over 15 years, BuyVistas has been restoring the beauty of homes and 
              businesses across the New York metropolitan area.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Our Story
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                From Humble Beginnings to Industry Leaders
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                BuyVistas was founded in 2009 by James Mitchell, a passionate entrepreneur who 
                saw an opportunity to transform the pressure washing industry. Starting with 
                just a single pressure washer and a pickup truck, James built the company on 
                the principles of quality, integrity, and customer satisfaction.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Today, BuyVistas operates a fleet of state-of-the-art equipment and employs 
                a team of certified professionals. We've completed over 5,000 projects and 
                continue to grow, but our commitment to personalized service remains unchanged.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our success is built on the trust of our customers. Whether it's a small 
                residential driveway or a large commercial complex, every job receives the 
                same attention to detail and dedication to excellence.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1552197717-fdea1c70c5c7?q=80&w=1200"
                alt="BuyVistas team"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-sky-600 text-white p-6">
                <div className="text-4xl font-extrabold mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>2009</div>
                <div className="text-sm uppercase tracking-wider">Year Founded</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="section-padding bg-slate-900" data-testid="about-experience">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { number: "15+", label: "Years Experience" },
              { number: "5000+", label: "Projects Completed" },
              { number: "100%", label: "Satisfaction Rate" },
              { number: "50+", label: "Team Members" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-5xl font-extrabold text-white mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {stat.number}
                </div>
                <div className="text-slate-400 uppercase tracking-wider text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="section-padding bg-slate-50" data-testid="about-mission">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
              Our Mission
            </span>
            <h2 className="heading-secondary text-slate-900 mb-6">
              Dedicated to Excellence in Every Clean
            </h2>
            <p className="text-slate-600 text-lg">
              Our mission is to deliver exceptional pressure washing services that enhance 
              property value, improve aesthetics, and protect your investment. We achieve 
              this through professional expertise, cutting-edge equipment, and an unwavering 
              commitment to customer satisfaction.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border border-slate-200 text-center"
              >
                <div className="w-14 h-14 bg-sky-100 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white" data-testid="about-team">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
              Our Team
            </span>
            <h2 className="heading-secondary text-slate-900 mb-6">
              Meet the Professionals Behind BuyVistas
            </h2>
            <p className="text-slate-600">
              Our team consists of certified, experienced professionals who are passionate 
              about delivering outstanding results for every customer.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-32 h-32 bg-slate-200 mx-auto mb-6 flex items-center justify-center">
                  <Users className="w-16 h-16 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {member.name}
                </h3>
                <p className="text-sky-600 font-semibold text-sm mb-3 uppercase tracking-wider">
                  {member.role}
                </p>
                <p className="text-slate-600 text-sm">
                  {member.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="section-padding bg-slate-50" data-testid="about-commitment">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200"
                alt="Clean home exterior"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Our Commitment
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                100% Customer Satisfaction Guaranteed
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                At BuyVistas, customer satisfaction isn't just a goal—it's our guarantee. 
                We stand behind every project with our 100% satisfaction promise. If you're 
                not completely happy with our work, we'll re-clean the area at no additional cost.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Licensed, bonded, and fully insured",
                  "Background-checked and trained technicians",
                  "Eco-friendly cleaning solutions",
                  "Transparent pricing with no hidden fees",
                  "Responsive customer support"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/contact" className="btn-primary inline-block" data-testid="about-contact-btn">
                <span>Contact Us Today</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16" data-testid="about-cta">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-secondary text-white mb-4">
                Ready to Experience the BuyVistas Difference?
              </h2>
              <p className="text-slate-300 text-lg">
                Join thousands of satisfied customers across New York.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="about-quote-btn">
                <span>Get Free Quote</span>
              </Link>
              <a href="tel:+1-888-435-4674" className="btn-secondary" data-testid="about-call-btn">
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  +1-888-435-4674
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
